/**
 * One thing for sale, shown as what you get rather than as a line of text.
 *
 * The store listed a name, a sentence and a price. Nobody buys a deck from a
 * sentence — it is the one product in here whose entire value is what it looks
 * like, and the shop was describing it. So the card leads with the art: three
 * faces fanned over the table they are dealt on.
 *
 * ## The preview is the real thing, not a mock-up of it
 *
 * The cards drawn here are the same `<img>` files the table deals, at the same
 * aspect, over the same table image. That is deliberate and worth the few
 * extra requests: a painted preview would be a promise somebody else has to
 * keep, and the first time a deck looked better in the shop than at the table
 * we would have taught people not to trust the shop.
 *
 * ## Why the ace, a court and a number
 *
 * They are the three kinds of card in a deck, and a theme can be good at one
 * and poor at another — an ace is a poster, a court is a portrait, a number is
 * a legibility test. Showing three answers the question somebody actually has,
 * which is "what will my hand look like".
 */

import { formatPrice } from '../net/session.js';

export interface ThemeOffer {
  /** What is being sold: a single theme, or a pack that grants several. */
  id: string;
  name: string;
  description: string;
  priceCents: number;
  owned: boolean;
  /** Deck ids to draw. One for a theme, two for a holiday pair. */
  deckIds: string[];
  /** How many faces, tables and characters come with it. */
  contents: string;
}

/** The three cards a preview shows, and why: poster, portrait, legibility. */
const SHOWN = ['AS', 'KH', '7C'];

export function ThemeCard({
  offer,
  currency,
  busy,
  onBuy,
}: {
  offer: ThemeOffer;
  currency: string;
  busy: boolean;
  onBuy: () => void;
}) {
  /* A pair shows its first deck; the second is named in the contents line
     rather than drawn, because two fans of three in one tile is a jumble and
     the point of the tile is to be read at a glance. */
  const deckId = offer.deckIds[0] ?? 'classic';

  return (
    <article className="theme-card">
      {/*
        A 640px crop of the table, not the table itself.
        
        The real wide composition is about 270KB, and seven of them is a store
        page that costs three megabytes to look at - on a phone, before you
        have decided to buy anything. The tile is 300px across at its widest,
        so the full-size art was being downloaded to be thrown away.
      */}
      <div
        className="theme-preview"
        style={{ backgroundImage: `url("/art/table-${deckId}-preview.webp")` }}
        aria-hidden="true"
      >
        <span className="theme-fan">
          {SHOWN.map((card, index) => (
            <img
              key={card}
              className="theme-fan-card"
              src={`/art/deck/${deckId}/${card}.webp`}
              alt=""
              loading="lazy"
              style={{ '--i': String(index) } as React.CSSProperties}
            />
          ))}
        </span>
      </div>

      <div className="theme-text">
        <h3 className="theme-name">{offer.name}</h3>
        <p className="theme-contents">{offer.contents}</p>
        <p className="theme-blurb">{offer.description}</p>
      </div>

      {offer.owned ? (
        <span className="owned-mark">Yours</span>
      ) : (
        <button type="button" className="pill-button buy" disabled={busy} onClick={onBuy}>
          {busy ? 'Getting…' : formatPrice(offer.priceCents, currency)}
        </button>
      )}
    </article>
  );
}
